// src/sectors/pilates/marketing/pages/ServicesPage.jsx
import React from 'react';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

// küçük rozet
function Pill({ icon, label }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 16, opacity: 0.9 }}>{icon}</Icon>}
      label={label}
      size="small"
      variant="outlined"
      sx={{
        borderRadius: '999px',
        fontWeight: 900,
        bgcolor: isDark ? alpha('#0b1220', 0.18) : alpha('#fff', 0.7),
        borderColor: alpha(theme.palette.info.main, isDark ? 0.25 : 0.18),
      }}
    />
  );
}

function Feature({ text }) {
  const theme = useMuiTheme();
  return (
    <MDBox sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <MDBox
        sx={{
          mt: '2px',
          width: 22,
          height: 22,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(theme.palette.success.main, 0.12),
          border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
          flex: '0 0 auto',
        }}
      >
        <Icon sx={{ fontSize: 16, color: theme.palette.success.main }}>done</Icon>
      </MDBox>
      <MDTypography variant="button" sx={{ fontWeight: 800, opacity: 0.9, lineHeight: 1.4 }}>
        {text}
      </MDTypography>
    </MDBox>
  );
}

function ServiceCard({ icon, title, subtitle, tags = [], features = [], ctaLabel, onCta }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Card
      sx={{
        borderRadius: '22px',
        border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
        boxShadow: theme.shadows[8],
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <MDBox
        sx={{
          p: 2.25,
          background: isDark
            ? `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.18)}, ${alpha(
                '#000',
                0
              )})`
            : `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.12)}, ${alpha(
                '#fff',
                0.8
              )})`,
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1.25}>
          <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
            <MDBox
              sx={{
                width: 44,
                height: 44,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.info.main, isDark ? 0.16 : 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, isDark ? 0.22 : 0.18)}`,
                flex: '0 0 auto',
              }}
            >
              <Icon sx={{ fontSize: 22, opacity: 0.95 }}>{icon}</Icon>
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
                {title}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                {subtitle}
              </MDTypography>
            </MDBox>
          </MDBox>

          <Icon sx={{ opacity: 0.25, fontSize: 28 }}>auto_awesome</Icon>
        </MDBox>

        {tags.length ? (
          <MDBox sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            {tags.map((t) => (
              <Pill key={t.label} icon={t.icon} label={t.label} />
            ))}
          </MDBox>
        ) : null}
      </MDBox>

      <Divider />

      <MDBox sx={{ p: 2.25, display: 'grid', gap: 1.1 }}>
        {features.map((f) => (
          <Feature key={f} text={f} />
        ))}

        <MDBox sx={{ pt: 0.6 }}>
          <MDButton
            fullWidth
            variant="contained"
            color="info"
            onClick={onCta}
            sx={{ borderRadius: '16px', fontWeight: 900, textTransform: 'none', py: 1.05 }}
          >
            {ctaLabel}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default function ServicesPage() {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = React.useState({
    name: '',
    phone: '',
    business: '',
    service: 'WEBSITE',
    note: '',
  });

  const headerBg = isDark
    ? `radial-gradient(900px 420px at 20% 0%, ${alpha(theme.palette.info.main, 0.28)} 0%, ${alpha(
        '#000',
        0
      )} 60%),
       radial-gradient(900px 420px at 80% 20%, ${alpha(
         theme.palette.success.main,
         0.14
       )} 0%, ${alpha('#000', 0)} 62%)`
    : `radial-gradient(900px 420px at 20% 0%, ${alpha(theme.palette.info.main, 0.18)} 0%, ${alpha(
        '#fff',
        0
      )} 60%),
       radial-gradient(900px 420px at 80% 20%, ${alpha(
         theme.palette.success.main,
         0.1
       )} 0%, ${alpha('#fff', 0)} 62%)`;

  const services = [
    {
      key: 'WEBSITE',
      icon: 'language',
      title: 'Web Sitesi Tasarımı',
      subtitle: 'Hızlı, modern, mobil uyumlu web sitesi',
      tags: [
        { icon: 'bolt', label: 'Hızlı teslim' },
        { icon: 'phone_iphone', label: 'Mobil uyum' },
        { icon: 'search', label: 'SEO temel' },
      ],
      features: [
        'Tek sayfa veya çok sayfa tasarım (kurumsal / stüdyo)',
        'sms tıkla-ara, harita, iletişim formu',
        'Foto/galeri alanı + paket/plan tanıtımı',
        'Temel SEO + Google Business uyumu',
      ],
      ctaLabel: 'Web sitesi teklifi al',
    },
    {
      key: 'SOCIAL',
      icon: 'campaign',
      title: 'Sosyal Medya Danışmanlığı',
      subtitle: 'İçerik planı, görsel dil, büyüme stratejisi',
      tags: [
        { icon: 'calendar_month', label: 'Aylık içerik planı' },
        { icon: 'palette', label: 'Marka dili' },
        { icon: 'trending_up', label: 'Büyüme' },
      ],
      features: [
        'Instagram içerik planı + haftalık yayın akışı',
        'Canva şablon seti (post/story/reels kapak)',
        'DM/yorum dönüş metinleri + satış akışı',
        'Analiz raporu: neler tuttu / neyi değiştirelim',
      ],
      ctaLabel: 'Danışmanlık görüşmesi al',
    },
    {
      key: 'SOFTWARE',
      icon: 'apps',
      title: 'İşletme Yazılım Çözümleri',
      subtitle: 'Seans, üye, tahsilat, bildirim, raporlama',
      tags: [
        { icon: 'event_available', label: 'Takvim & seans' },
        { icon: 'payments', label: 'Tahsilat' },
        { icon: 'insights', label: 'Raporlar' },
      ],
      features: [
        'Üye / paket / borç takibi + tahsilat akışı',
        'Seans planlama + yoklama + telafi mantığı',
        'Eğitmen yetkileri + işletme sahiplerine raporlar',
        'Bildirim otomasyonları (email/sms/sms aşamalı)',
      ],
      ctaLabel: 'Demo & ihtiyaç analizi',
    },
  ];

  const pickService = (key) => {
    setForm((p) => ({ ...p, service: key }));
    // istersen sayfayı forma kaydırabilirsin
    const el = document.getElementById('services-lead-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onSubmit = () => {
    // şimdilik console — sonra backend’e bağlarız
    // eslint-disable-next-line no-console
    console.log('[Services Lead]', form);
    alert('Talebin alındı. En kısa sürede dönüş yapacağız.');
  };

  return (
    <MDBox sx={{ width: '100%' }}>
      {/* HERO */}
      <MDBox
        sx={{
          borderRadius: '24px',
          border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
          boxShadow: theme.shadows[10],
          overflow: 'hidden',
          background: headerBg,
          p: { xs: 2, sm: 2.5, md: 3 },
          mt: 2,
        }}
      >
        <MDBox sx={{ display: 'grid', gap: 1 }}>
          <MDTypography
            variant={isMobile ? 'h5' : 'h4'}
            fontWeight="bold"
            sx={{ lineHeight: 1.15 }}
          >
            Hizmetler
          </MDTypography>

          <MDTypography
            variant="button"
            sx={{ color: 'text.secondary', fontWeight: 700, maxWidth: 860 }}
          >
            Web sitesi, sosyal medya ve işletme yazılımı ile stüdyonun satışını ve operasyonunu
            hızlandırıyoruz. “Az efor, net sonuç” odaklı ilerliyoruz.
          </MDTypography>

          <MDBox sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            <Pill icon="verified" label="Profesyonel görünüm" />
            <Pill icon="task_alt" label="Net kapsam" />
            <Pill icon="support_agent" label="Sürekli destek" />
          </MDBox>
        </MDBox>
      </MDBox>

      {/* SERVICES GRID */}
      <MDBox
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {services.map((s) => (
          <ServiceCard
            key={s.key}
            icon={s.icon}
            title={s.title}
            subtitle={s.subtitle}
            tags={s.tags}
            features={s.features}
            ctaLabel={s.ctaLabel}
            onCta={() => pickService(s.key)}
          />
        ))}
      </MDBox>

      {/* LEAD FORM */}
      <Card
        id="services-lead-form"
        sx={{
          mt: 2,
          borderRadius: '22px',
          border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
          boxShadow: theme.shadows[8],
          overflow: 'hidden',
        }}
      >
        <MDBox
          sx={{
            p: 2.25,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.25,
            flexDirection: { xs: 'column', sm: 'row' },
            background: isDark ? alpha('#0b1220', 0.28) : alpha(theme.palette.info.main, 0.06),
          }}
        >
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold">
              Teklif / Görüşme Talebi
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
              İhtiyacını yaz — sana en uygun paketi netleştirelim.
            </MDTypography>
          </MDBox>

          <Chip
            size="small"
            variant="outlined"
            label={
              form.service === 'WEBSITE'
                ? 'Seçili: Web Sitesi'
                : form.service === 'SOCIAL'
                ? 'Seçili: Sosyal Medya'
                : 'Seçili: Yazılım'
            }
            sx={{ borderRadius: '999px', fontWeight: 900 }}
          />
        </MDBox>

        <Divider />

        <MDBox
          sx={{
            p: 2.25,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.25,
          }}
        >
          <MDInput
            label="Ad Soyad"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <MDInput
            label="Telefon / sms"
            value={form.phone}
            onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
          />
          <MDInput
            label="İşletme Adı"
            value={form.business}
            onChange={(e) => setForm((p) => ({ ...p, business: e.target.value }))}
          />
          <MDInput
            label="Not"
            value={form.note}
            onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
          />

          <MDBox sx={{ gridColumn: { xs: 'auto', md: '1 / -1' }, pt: 0.5 }}>
            <MDButton
              fullWidth
              variant="contained"
              color="info"
              onClick={onSubmit}
              sx={{
                borderRadius: '16px',
                fontWeight: 900,
                textTransform: 'none',
                py: 1.1,
              }}
            >
              Talep Gönder
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      {/* FAQ */}
      <Card
        sx={{
          mt: 2,
          mb: 2,
          borderRadius: '22px',
          border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
          boxShadow: theme.shadows[6],
          overflow: 'hidden',
        }}
      >
        <MDBox sx={{ p: 2.25 }}>
          <MDTypography variant="h6" fontWeight="bold">
            Sık Sorulanlar
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
            En çok sorulan 3 konu.
          </MDTypography>
        </MDBox>
        <Divider />

        <MDBox sx={{ p: 1.5 }}>
          {[
            {
              q: 'Web sitesi ne kadar sürede çıkar?',
              a: 'İçerik hazırsa tek sayfa tasarım hızlı çıkar; kurumsal çok sayfa projelerde kapsam belirleyip net takvim çıkarıyoruz.',
            },
            {
              q: 'Sosyal medya danışmanlığında neler veriyorsunuz?',
              a: 'Aylık içerik planı, görsel şablon seti, satış/DM akışı ve performans raporu. İsterseniz çekim günü planı da ekleriz.',
            },
            {
              q: 'Yazılım çözümleri kimlere uygun?',
              a: 'Seans/paket/borç takibi, yoklama, tahsilat ve raporlama ihtiyacı olan stüdyolar için. Modüler ilerleyip ihtiyaç oldukça açıyoruz.',
            },
          ].map((x) => (
            <Accordion
              key={x.q}
              elevation={0}
              sx={{
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08)}`,
                '&:before': { display: 'none' },
                mb: 1,
                overflow: 'hidden',
              }}
            >
              <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                <MDTypography variant="button" fontWeight="bold">
                  {x.q}
                </MDTypography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <MDTypography variant="button" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  {x.a}
                </MDTypography>
              </AccordionDetails>
            </Accordion>
          ))}
        </MDBox>
      </Card>
    </MDBox>
  );
}
